import { store } from '../store';
import { updateMessageMetadata } from '../store/slices/coworkSlice';
import { getTargetClient, relayService } from './relay';

export type ResultFeedback = 'helpful' | 'not_helpful' | null;

/** 捕获当前登录周期，只用于内存比较，不写日志或持久化。 */
function captureIdentity(): () => void {
  const token = localStorage.getItem('boclaw_token');
  const userId = localStorage.getItem('boclaw_user_id');
  return () => {
    if (token !== localStorage.getItem('boclaw_token') || userId !== localStorage.getItem('boclaw_user_id')) throw new Error('Account changed');
  };
}

/** 校验 Web 反馈成功 DTO；空响应或错误结构不能变成已提交状态。 */
function feedbackMetadata(value: unknown, expectedFeedback?: ResultFeedback, opinion = false): Record<string, unknown> {
  const result = value as { success?: unknown; metadata?: unknown } | null;
  if (result?.success !== true || !result.metadata || typeof result.metadata !== 'object' || Array.isArray(result.metadata)) {
    throw new Error('Invalid feedback response');
  }
  const metadata = result.metadata as Record<string, unknown>;
  if (metadata.resultFeedback !== null && metadata.resultFeedback !== 'helpful' && metadata.resultFeedback !== 'not_helpful') {
    throw new Error('Invalid feedback metadata');
  }
  if (expectedFeedback !== undefined && metadata.resultFeedback !== expectedFeedback) throw new Error('Feedback was not confirmed');
  if (opinion && metadata.resultOpinionSubmitted !== true) throw new Error('Opinion was not confirmed');
  return metadata;
}

/** 提交或撤销评价；等待所属端确认后才更新当前消息元数据。 */
export async function setResultFeedback(
  sessionId: string,
  messageId: string,
  messageTimestamp: number,
  feedback: ResultFeedback,
  desktop: boolean,
): Promise<void> {
  const assertCurrent = captureIdentity();
  if (desktop) {
    const target = getTargetClient(sessionId);
    if (!target) throw new Error('Desktop is offline');
    await relayService.ensureConnected();
    assertCurrent();
    await relayService.setResultFeedback(target, sessionId, messageId, messageTimestamp, feedback);
    assertCurrent();
    store.dispatch(updateMessageMetadata({ sessionId, messageId, metadata: {
      resultFeedback: feedback,
      resultOpinionSubmitted: false,
      resultOpinionTagCodes: [],
      resultOpinionContent: '',
    } }));
    return;
  }
  const result = await window.electron.cowork.setResultFeedback(sessionId, messageId, feedback);
  assertCurrent();
  store.dispatch(updateMessageMetadata({ sessionId, messageId, metadata: feedbackMetadata(result, feedback) }));
}

/** 读取意见标签，忽略无效条目并拒绝跨登录周期的迟到列表。 */
export async function getResultOpinionTags(sessionId: string, desktop: boolean): Promise<Array<{ code: string; name: string }>> {
  const assertCurrent = captureIdentity();
  const target = desktop ? getTargetClient(sessionId) : null;
  if (desktop && !target) throw new Error('Desktop is offline');
  const result = desktop
    ? { success: true, tags: await relayService.getResultOpinionTags(target!) }
    : await window.electron.cowork.getResultOpinionTags(sessionId) as { success?: unknown; tags?: unknown };
  assertCurrent();
  if (result?.success !== true || !Array.isArray(result.tags)) throw new Error('Invalid feedback tags');
  return result.tags.filter((tag): tag is { code: string; name: string } => Boolean(tag)
    && typeof tag.code === 'string' && typeof tag.name === 'string');
}

/** 提交结构化意见；REST 与 Relay 都须在身份校验后才能影响 Redux。 */
export async function submitResultOpinion(
  sessionId: string,
  messageId: string,
  messageTimestamp: number,
  tagCodes: string[],
  content: string,
  desktop: boolean,
): Promise<void> {
  const assertCurrent = captureIdentity();
  const target = desktop ? getTargetClient(sessionId) : null;
  if (desktop) {
    if (!target) throw new Error('Desktop is offline');
    const metadata = await relayService.submitResultOpinion(target, sessionId, messageId, messageTimestamp, tagCodes, content);
    assertCurrent();
    store.dispatch(updateMessageMetadata({ sessionId, messageId, metadata }));
    return;
  }
  const result = await window.electron.cowork.submitResultOpinion(sessionId, messageId, tagCodes, content);
  assertCurrent();
  store.dispatch(updateMessageMetadata({ sessionId, messageId, metadata: feedbackMetadata(result, undefined, true) }));
}
